/**
 * Simple animation hook for Flowi
 * Integrates with existing ViewItem and Connector types
 */

import { useCallback, useRef, useEffect } from 'react';
import { ViewItem } from 'src/types';
import { animationSystem, AnimationSystem } from './AnimationSystem';
import React from 'react';

export interface AnimationOptions {
    duration?: number;
    easing?: keyof typeof AnimationSystem.easings;
    onComplete?: () => void;
}

export function useAnimation() {
    const animationIds = useRef<Set<string>>(new Set());
    // Store updates automatically trigger re-renders
    const forceUpdate = React.useReducer(() => ({}), {})[1];

    // Clean up animations on unmount
    useEffect(() => {
        return () => {
            animationIds.current.forEach(id => {
                animationSystem.stop(id);
            });
        };
    }, []);

    /**
     * Animate a ViewItem's position
     */
    const animatePosition = useCallback((
        viewItem: ViewItem,
        to: { x?: number; y?: number },
        options: AnimationOptions = {}
    ) => {
        const animations: string[] = [];

        if (to.x !== undefined) {
            const id = animationSystem.animate(
                viewItem.tile,
                'x',
                to.x,
                {
                    duration: options.duration,
                    easing: options.easing ? AnimationSystem.easings[options.easing] : undefined,
                    onUpdate: () => forceUpdate(), // Trigger re-render
                    onComplete: options.onComplete
                }
            );
            animations.push(id);
        }

        if (to.y !== undefined) {
            const id = animationSystem.animate(
                viewItem.tile,
                'y',
                to.y,
                {
                    duration: options.duration,
                    easing: options.easing ? AnimationSystem.easings[options.easing] : undefined,
                    onUpdate: () => forceUpdate()
                }
            );
            animations.push(id);
        }

        animations.forEach(id => animationIds.current.add(id));
        return animations;
    }, [forceUpdate]);

    /**
     * Pulse animation for ViewItem
     */
    const pulse = useCallback((
        viewItem: ViewItem,
        scale: number = 1.1,
        duration: number = 1000
    ) => {
        // Create a pulse effect by animating labelHeight
        const originalHeight = viewItem?.labelHeight ?? 0;
        const id = animationSystem.animate(
            viewItem,
            'labelHeight',
            originalHeight * scale,
            {
                duration: duration / 2,
                easing: AnimationSystem.easings.easeOutQuad,
                onUpdate: () => forceUpdate(),
                onComplete: () => {
                    // Animate back
                    animationSystem.animate(
                        viewItem,
                        'labelHeight',
                        originalHeight,
                        {
                            duration: duration / 2,
                            easing: AnimationSystem.easings.easeInQuad,
                            onUpdate: () => forceUpdate()
                        }
                    );
                }
            }
        );

        animationIds.current.add(id);
        return id;
    }, [forceUpdate]);

    /**
     * Bounce animation
     */
    const bounce = useCallback((
        viewItem: ViewItem,
        height: number = 20,
        duration: number = 600
    ) => {
        const originalY = viewItem.tile.y;

        const id = animationSystem.animate(
            viewItem.tile,
            'y',
            originalY - height,
            {
                duration: duration / 2,
                easing: AnimationSystem.easings.easeOutQuad,
                onUpdate: () => forceUpdate(),
                onComplete: () => {
                    animationSystem.animate(
                        viewItem.tile,
                        'y',
                        originalY,
                        {
                            duration: duration / 2,
                            easing: AnimationSystem.easings.easeOutBounce,
                            onUpdate: () => forceUpdate()
                        }
                    );
                }
            }
        );

        animationIds.current.add(id);
        return id;
    }, [forceUpdate]);

    /**
     * Shake animation
     */
    const shake = useCallback((
        viewItem: ViewItem,
        intensity: number = 5,
        duration: number = 500
    ) => {
        const originalX = viewItem.tile.x;
        const shakes = 10;
        let currentShake = 0;

        const doShake = () => {
            if (currentShake >= shakes) {
                viewItem.tile.x = originalX;
                forceUpdate();
                return;
            }

            const offset = (Math.random() - 0.5) * intensity * (1 - currentShake / shakes);
            viewItem.tile.x = originalX + offset;
            forceUpdate();

            currentShake++;
            setTimeout(doShake, duration / shakes);
        };

        doShake();
        return 'shake-manual';
    }, [forceUpdate]);

    /**
     * Fade in (using labelHeight as a proxy for opacity)
     */
    const fadeIn = useCallback((
        viewItem: ViewItem,
        duration: number = 500
    ) => {
        const targetHeight = viewItem.labelHeight ?? 0;
        viewItem.labelHeight = 0;

        const id = animationSystem.animate(
            viewItem,
            'labelHeight',
            targetHeight,
            {
                duration,
                easing: AnimationSystem.easings.easeOutCubic,
                onUpdate: () => forceUpdate()
            }
        );

        animationIds.current.add(id);
        return id;
    }, [forceUpdate]);

    /**
     * Slide in from direction
     */
    const slideIn = useCallback((
        viewItem: ViewItem,
        from: 'left' | 'right' | 'top' | 'bottom',
        distance: number = 50,
        duration: number = 600
    ) => {
        const originalPosition = { ...viewItem.tile };

        // Set initial position
        switch (from) {
            case 'left':
                viewItem.tile.x -= distance;
                break;
            case 'right':
                viewItem.tile.x += distance;
                break;
            case 'top':
                viewItem.tile.y -= distance;
                break;
            case 'bottom':
                viewItem.tile.y += distance;
                break;
        }

        // Animate to original position
        return animatePosition(viewItem, originalPosition, {
            duration,
            easing: 'easeOutCubic'
        });
    }, [animatePosition]);

    /**
     * Highlight effect (pulse with color change simulation)
     */
    const highlight = useCallback((
        viewItem: ViewItem,
        pulses: number = 2,
        duration: number = 300
    ) => {
        let count = 0;

        const doPulse = () => {
            if (count >= pulses) return;

            pulse(viewItem, 1.2, duration);
            count++;

            if (count < pulses) {
                setTimeout(doPulse, duration);
            }
        };

        doPulse();
        return 'highlight-manual';
    }, [pulse]);

    /**
     * Stagger animation for multiple items
     */
    const stagger = useCallback((
        viewItems: ViewItem[],
        animation: 'fadeIn' | 'bounce' | 'slideIn',
        delay: number = 100,
        options: any = {}
    ) => {
        viewItems.forEach((item, index) => {
            setTimeout(() => {
                switch (animation) {
                    case 'fadeIn':
                        fadeIn(item, options.duration);
                        break;
                    case 'bounce':
                        bounce(item, options.height, options.duration);
                        break;
                    case 'slideIn':
                        slideIn(item, options.from || 'left', options.distance, options.duration);
                        break;
                }
            }, index * delay);
        });
    }, [fadeIn, bounce, slideIn]);

    /**
     * Create a ripple effect from a point
     */
    const ripple = useCallback((
        center: { x: number; y: number },
        affectedItems: ViewItem[],
        maxDistance: number = 100,
        duration: number = 1000
    ) => {
        affectedItems.forEach(item => {
            const dx = item.tile.x - center.x;
            const dy = item.tile.y - center.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= maxDistance) {
                const delay = (distance / maxDistance) * (duration / 2);

                setTimeout(() => {
                    bounce(item, 15 * (1 - distance / maxDistance), 400);
                }, delay);
            }
        });
    }, [bounce]);

    /**
     * Attention-grabbing animation
     */
    const attention = useCallback((
        viewItem: ViewItem,
        //options: AnimationOptions = {}
    ) => {
        // Combine shake and pulse for attention
        shake(viewItem, 3, 200);
        setTimeout(() => {
            pulse(viewItem, 1.15, 400);
        }, 200);
    }, [shake, pulse]);

    /**
     * Stop all animations
     */
    const stopAll = useCallback(() => {
        animationIds.current.forEach(id => {
            animationSystem.stop(id, false);
        });
        animationIds.current.clear();
    }, []);

    return {
        // Basic animations
        animatePosition,
        fadeIn,
        pulse,
        shake,
        bounce,
        slideIn,
        highlight,

        // Group animations
        stagger,
        ripple,
        attention,

        // Control
        stopAll,

        // Direct access to animation system
        animationSystem
    };
}